'use client';

import React from 'react';
import { useRouter } from 'next/navigation';
import { Box, type SxProps, type Theme, Typography, useTheme } from '@mui/material';

import { Avatar, Logo, SquareIcon } from '@/components';
import { isDarkColor } from '@/utils';

interface MarketCardHeaderProps {
  title: string;
  imageUrl?: string;
  action?: React.ReactNode;
  sx?: SxProps<Theme>;
  /** Primary event tag (e.g. "Commodities", "Politics") shown under the title. */
  category?: string;
  /** Secondary series ticker (e.g. "AUG", "H.R.3633") appended after the category. */
  seriesTicker?: string;
  isGame?: boolean;
  team1?: {
    abbr: string;
    name: string;
    color: string;
    logo?: string;
    percentage?: string;
  };
  /** Second team data (usually Away) */
  team2?: {
    abbr: string;
    name: string;
    color: string;
    logo?: string;
    percentage?: string;
  };
  gameUrl?: string;
  isResolved?: boolean;
}

function TeamRow({
  team,
  percentage,
  gameUrl,
  isResolved,
}: {
  team?: MarketCardHeaderProps['team1'];
  percentage: string;
  gameUrl?: string;
  isResolved?: boolean;
}) {
  const theme = useTheme();
  const router = useRouter();

  if (!team) return null;

  const badgeColor = team.color || theme.palette.primary.main;

  const handleTeamClick = (e: React.MouseEvent) => {
    if (gameUrl) {
      e.preventDefault();
      e.stopPropagation();
      router.push(gameUrl);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
        <Avatar
          src={team.logo}
          alt={team.name || team.abbr}
          size={32}
          fallbackLabel={team.abbr}
          sx={{
            borderRadius: '4px',
            bgcolor: team.logo ? 'transparent' : badgeColor,
            color: isDarkColor(badgeColor) ? '#ffffff' : '#000000',
            '& .MuiTypography-root': {
              fontSize: '0.65rem',
              fontWeight: 800,
              color: 'inherit',
            },
          }}
        />
        <Typography
          onClick={handleTeamClick}
          sx={{
            fontWeight: 600,
            fontSize: '0.95rem',
            color: 'text.primary',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            cursor: gameUrl ? 'pointer' : 'default',
            '&:hover': {
              textDecoration: gameUrl ? 'underline' : 'none',
              color: gameUrl ? 'primary.main' : 'text.primary',
            },
          }}
        >
          {team.name}
        </Typography>
      </Box>
      {!isResolved && (
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '1rem',
            color: 'text.primary',
            flexShrink: 0,
            ml: 1,
          }}
        >
          {percentage}
        </Typography>
      )}
    </Box>
  );
}

export function MarketCardHeader({
  title,
  imageUrl,
  action,
  sx,
  category,
  seriesTicker,
  isGame,
  team1,
  team2,
  gameUrl,
  isResolved,
}: MarketCardHeaderProps) {
  const subLabel = [category, seriesTicker].filter(Boolean).join(' · ');

  return !isGame ? (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5, // 12px (design)
        ...sx,
      }}
    >
      <SquareIcon src={imageUrl} alt={title} size={38} borderRadius="10px" />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="h6"
          component="h3"
          sx={{
            // Design: Archivo 600, 15.5px, line-height 1.3, tight tracking.
            fontFamily: 'var(--font-archivo), Archivo, sans-serif',
            fontWeight: 600,
            lineHeight: 1.3,
            letterSpacing: '-0.005em',
            color: 'text.primary',
            fontSize: '15.5px',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </Typography>
        {subLabel && (
          <Typography
            sx={{
              fontSize: '11px',
              fontWeight: 600,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.03em',
              mt: '4px',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
          >
            {subLabel}
          </Typography>
        )}
      </Box>
      {action && <Box sx={{ flexShrink: 0, ml: 1 }}>{action}</Box>}
    </Box>
  ) : (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        ...sx,
      }}
    >
      <TeamRow
        team={team1}
        percentage={team1?.percentage || '--'}
        gameUrl={gameUrl}
        isResolved={isResolved}
      />
      <TeamRow
        team={team2}
        percentage={team2?.percentage || '--'}
        gameUrl={gameUrl}
        isResolved={isResolved}
      />
    </Box>
  );
}
